import React, { useState, useEffect } from 'react';
import { supabase } from '../../supabaseClient';

const AdminFiles = () => {
  const [files, setFiles] = useState([]);
  const [uploading, setUploading] = useState(false);
  const [selectedFile, setSelectedFile] = useState(null);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const { data: filesData, error: filesError } = await supabase
        .from('files')
        .select('*')
        .eq('type', 'Brochure')
        .order('created_at', { ascending: false });
        
      if (filesError) {
        console.error('Error fetching files:', filesError);
        alert('Error fetching files: ' + filesError.message);
      } else {
        setFiles(filesData || []);
      }
    } catch (e) {
      console.error('Exception in fetchData:', e);
    }
  };

  const handleFileChange = (e) => {
    if (e.target.files && e.target.files.length > 0) {
      setSelectedFile(e.target.files[0]);
    }
  };

  const handleUpload = async (e) => {
    e.preventDefault();
    if (!selectedFile) {
      alert("Please select a file to upload.");
      return;
    }

    setUploading(true);
    
    try {
      const fileExt = selectedFile.name.split('.').pop();
      const fileName = `${Math.random()}.${fileExt}`;
      const filePath = `brochure/${fileName}`;
      
      const { error: uploadError } = await supabase.storage
        .from('hbb_files')
        .upload(filePath, selectedFile);
        
      if (uploadError) throw uploadError;

      const { data: { publicUrl } } = supabase.storage
        .from('hbb_files')
        .getPublicUrl(filePath);

      const { error: dbError } = await supabase
        .from('files')
        .insert([{
          name: selectedFile.name,
          url: publicUrl,
          type: 'Brochure',
          related_project_id: null
        }]);
        
      if (dbError) throw dbError;
      
      setSelectedFile(null);
      fetchData();
    } catch (error) {
      console.error('Error during upload process:', error);
      alert('Upload failed: ' + error.message);
    } finally {
      setUploading(false);
    }
  };

  const handleDelete = async (id) => {
    if (window.confirm('Are you sure you want to delete this file?')) {
      try {
        const { error } = await supabase.from('files').delete().eq('id', id);
        if (error) throw error;
        fetchData();
      } catch (error) {
        console.error('Error deleting file:', error);
        alert('Delete failed: ' + error.message);
      }
    }
  };

  return (
    <div className="p-8 min-h-screen bg-[#131313] text-white">
      <h2 className="text-3xl font-bold mb-6 text-[#f59e0b]" style={{ fontFamily: 'Bebas Neue' }}>Manage Brochure</h2>
      
      {files.length === 0 && (
        <div className="bg-[#1c1b1b] p-6 rounded-xl border border-white/10 mb-8 shadow-xl">
          <h3 className="text-xl font-semibold mb-4 text-[#d8c3ad]">Upload Global Brochure</h3>
          <form onSubmit={handleUpload} className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div className="md:col-span-1">
              <input 
                type="file" 
                onChange={handleFileChange} 
                className="bg-[#0e0e0e] border border-white/10 rounded px-4 py-2 text-white w-full" 
                required 
              />
            </div>
            
            <div className="md:col-span-1">
              <select disabled className="bg-[#0e0e0e] border border-white/10 rounded px-4 py-2 text-gray-500 w-full cursor-not-allowed">
                <option value="Brochure">Brochure</option>
              </select>
            </div>
            
            <button 
              type="submit" 
              disabled={uploading}
              className={`md:col-span-2 font-bold py-3 rounded transition-colors flex justify-center items-center gap-2 ${uploading ? 'bg-gray-600 text-gray-300 cursor-not-allowed' : 'bg-[#f59e0b] text-[#2a1700] hover:bg-[#ffc174]'}`}
            >
              {uploading ? (
                <>
                  <span className="material-symbols-outlined animate-spin text-sm">sync</span>
                  Processing...
                </>
              ) : (
                'Upload Brochure'
              )}
            </button>
          </form>
        </div>
      )}

      {files.length > 0 && (
        <div className="bg-[#1c1b1b] rounded-xl border border-white/10 overflow-hidden">
          <div className="p-4 bg-white/5 border-b border-white/10 flex justify-between items-center">
            <h3 className="text-lg font-semibold text-[#d8c3ad]">Current Brochure</h3>
            <span className="text-xs text-green-400 bg-green-400/10 px-2 py-1 rounded">Active</span>
          </div>
          <div className="overflow-x-auto">
            <table className="w-full text-left border-collapse min-w-[500px]">
              <thead>
                <tr className="border-b border-white/5 bg-[#0e0e0e]">
                  <th className="px-6 py-4 font-normal text-[#a08e7a]">File Name</th>
                  <th className="px-6 py-4 font-normal text-[#a08e7a]">Type</th>
                  <th className="px-6 py-4 font-normal text-[#a08e7a] text-right">Actions</th>
                </tr>
              </thead>
              <tbody>
                {files.map(file => (
                  <tr key={file.id} className="border-b border-white/5 hover:bg-white/5 transition-colors">
                    <td className="px-6 py-4 font-medium text-white flex items-center gap-3 whitespace-nowrap">
                      <span className="material-symbols-outlined text-[#f59e0b] text-sm">description</span>
                      {file.name}
                    </td>
                    <td className="px-6 py-4 text-gray-400 whitespace-nowrap">{file.type}</td>
                    <td className="px-6 py-4 text-right whitespace-nowrap">
                      <button 
                        onClick={() => handleDelete(file.id, file.url)} 
                        disabled={uploading}
                        className="p-2 bg-red-500/10 text-red-400 hover:bg-red-500/20 rounded transition-colors"
                        title="Delete Brochure"
                      >
                        <span className="material-symbols-outlined text-sm">delete</span>
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminFiles;
